import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Inbox, CalendarCheck, Building2, AlertTriangle, Plus, BarChart3, Sparkles, Search, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { StatusDonut } from "@/components/dashboard/StatusDonut";
import { TrendBars } from "@/components/dashboard/TrendBars";
import { UkLocationsMap } from "@/components/dashboard/UkLocationsMap";

const KPI_TILES = [
  { key: "new_enquiries", label: "New Enquiries", icon: Inbox, tile: "bg-[#EDF3FF] text-[#3157A4]", to: "/control-centre/enquiries?status=REQUEST" },
  { key: "active_bookings", label: "Active Bookings", icon: CalendarCheck, tile: "bg-[#E7F3EA] text-[#2E7D46]", to: "/control-centre/bookings" },
  { key: "clients", label: "Clients", icon: Building2, tile: "bg-[#FFF3E8] text-[#DF7916]", to: "/control-centre/clients" },
  { key: "action_required", label: "Action Required", icon: AlertTriangle, tile: "bg-[#FBEAEA] text-[#B23B3B]", to: "/control-centre/enquiries?status=REQUEST" },
];

// Six actions from the supplied creative, each mapped to an existing route. Bookings are enquiry-derived
// (READY_TO_BOOK -> Complete), so "New Booking" opens that queue rather than pretending to create one directly.
const QUICK_ACTIONS = [
  { label: "Add Enquiry", icon: Plus, to: "/control-centre/enquiries?new=1", testId: "qa-new-enquiry" },
  { label: "New Booking", sub: "via ready enquiries", icon: CalendarCheck, to: "/control-centre/enquiries?status=READY_TO_BOOK", testId: "qa-new-booking" },
  { label: "Add Client", icon: Building2, to: "/control-centre/clients", testId: "qa-add-client" },
  { label: "Add Guest", icon: UserPlus, to: "/control-centre/people", testId: "qa-add-guest" },
  { label: "Find Hotels", icon: Search, to: "/control-centre/properties", testId: "qa-find-hotels" },
  { label: "Create Report", icon: BarChart3, to: "/control-centre/reports", testId: "qa-reports" },
];

// Operational wording for audit actions; auth/session noise is excluded here (it stays in Admin -> Security audit).
const ACTIVITY_LABELS = {
  "enquiry.created": "New enquiry received", "enquiry.assigned": "Enquiry assigned", "enquiry.status_changed": "Enquiry status updated",
  "enquiry.completed": "Enquiry converted to booking", "supplier_offer.created": "Supplier offer added", "supplier_offer.submitted": "Supplier offer submitted", "allocation.unit_cleared": "Unit allocation cleared", "allocation.unit_assigned": "Unit allocated", "admin.ai_settings_updated": "AI settings updated", "client_offer.presented": "Offer presented to client",
  "client_offer.decided": "Client decided on an offer", "booking_change.requested": "Booking change requested", "booking_change.applied": "Booking change applied",
  "booking_change.rejected": "Booking change rejected", "booking.cancelled": "Booking cancelled", "stay.allocated": "Unit allocated", "stay.unallocated": "Unit allocation removed",
  "confirmation.generated": "Confirmation PDF issued", "communication.reviewed": "Communication reviewed", "communication.queued": "Communication queued",
  "communication.sent": "Communication accepted by Microsoft", "payment.recorded": "Ledger entry recorded", "payment.status_changed": "Ledger status changed",
  "document.uploaded": "Document uploaded", "compliance.document_recorded": "Compliance document recorded", "support.message": "Support message",
  "support.reply": "Support reply sent", "mailbox.connected": "Mailbox connected", "mailbox.disconnected": "Mailbox disconnected",
  "communication.resolved": "Communication resolved by staff", "communication.attempt.accepted": "E-mail accepted by Microsoft",
  "communication.attempt.retryable_error": "E-mail attempt failed - will retry", "communication.attempt.permanent_error": "E-mail attempt failed permanently",
  "communication.attempt.ambiguous": "E-mail attempt needs a staff decision", "xero.export.exported": "Xero invoice exported",
  "xero.export.failed": "Xero export failed", "xero.export.blocked_setup": "Xero export blocked - finance setup outstanding",
  "enquiry.missing_info_requested": "Client asked for missing information", "client.created": "Client organisation created", "client.updated": "Client details updated",
  "supplier.created": "Supplier created", "supplier.updated": "Supplier details updated", "document.access_granted": "Document access granted",
  "document.upload_rejected_infected": "Upload rejected by malware scan",
};
const describeActivity = (a) => ACTIVITY_LABELS[a.action] || (a.action.startsWith("auth.") ? null : a.action.replace(/^[a-z_]+\./, "").replace(/[._]/g, " "));

const Panel = ({ title, action, children, testId, className = "" }) => (
  <section data-testid={testId} className={`rounded-[10px] border border-jigsaw-border bg-white shadow-[0_1px_3px_rgba(16,24,47,0.05)] ${className}`}>
    <header className="flex items-center justify-between px-5 pt-4 pb-2">
      <h2 className="text-[15px] font-semibold text-jigsaw-navy">{title}</h2>
      {action}
    </header>
    <div className="px-5 pb-5">{children}</div>
  </section>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ai, setAi] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/dashboard/control-centre").then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/integrations").then((r) => setAi((r.data.items || r.data).find?.((i) => i.provider === "AI") || null)).catch(() => setAi(null));
  }, []);

  if (error) return <ErrorState testId="dashboard-error" />;
  if (!data) return <LoadingState testId="dashboard-loading" />;

  const firstName = user?.full_name?.split(" ")[0] || "there";
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const trendTotal = (data.enquiry_trend_30d || []).reduce((n, p) => n + p.count, 0);
  const totalBookings = (data.booking_status_breakdown || []).reduce((n, b) => n + b.count, 0);

  return (
    <div data-testid="dashboard-page" className="mx-auto max-w-[1560px]">
    <div className="flex flex-col gap-6 xl:flex-row">
      {/* ---------------- main column ---------------- */}
      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div>
            <h1 data-testid="dashboard-greeting" className="text-[26px] font-semibold sm:text-[32px] leading-tight tracking-[-0.01em] text-jigsaw-navy">Welcome back, {firstName}</h1>
            <p className="mt-1 text-[15px] text-jigsaw-muted">Here&rsquo;s what&rsquo;s happening across your bookings today.</p>
          </div>
          <p data-testid="dashboard-date" className="shrink-0 text-sm text-jigsaw-muted sm:text-right">{today}</p>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {KPI_TILES.map(({ key, label, icon: Icon, tile, to }) => (
            <button key={key} data-testid={`kpi-${key.replace(/_/g, "-")}`} onClick={() => navigate(to)}
              className="flex h-[104px] items-center gap-4 rounded-[10px] border border-jigsaw-border bg-white px-5 text-left shadow-[0_1px_3px_rgba(16,24,47,0.05)] transition-colors hover:border-jigsaw-orange/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-jigsaw-navy/20">
              <span className={`flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl ${tile}`}><Icon strokeWidth={1.75} className="h-6 w-6" /></span>
              <span className="min-w-0">
                <span className="block text-[13px] font-medium text-jigsaw-muted">{label}</span>
                <span className="block text-[32px] font-bold leading-none tracking-[-0.02em] text-jigsaw-navy">{data.kpis[key] ?? 0}</span>
              </span>
            </button>
          ))}
        </div>

        {data.historical_archive && (
          <section data-testid="dashboard-historical-archive" aria-label="Historical archive" className="rounded-[10px] border border-dashed border-jigsaw-border bg-jigsaw-bg px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-jigsaw-navy px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">Legacy archive</span>
                <h2 className="text-base font-semibold text-jigsaw-navy md:text-lg">Historical records (separate from the figures above)</h2>
              </div>
              <span data-testid="historical-snapshot-date" className="text-xs text-jigsaw-muted">Snapshot {new Date(data.historical_archive.snapshot_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[["bookings", "Bookings"], ["stays", "Stay entries"], ["clients", "Clients"], ["properties", "Properties"], ["guest_profiles", "Guest profiles"], ["bookings_on_hold", "On migration hold"]].map(([k, label]) => (
                <div key={k} data-testid={`historical-${k.replace(/_/g, "-")}`} className="rounded-lg border border-jigsaw-border bg-white px-3 py-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-jigsaw-muted">{label}</p>
                  <p className="text-xl font-bold leading-tight text-jigsaw-navy">{data.historical_archive[k].toLocaleString("en-GB")}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-jigsaw-muted">{data.historical_archive.disclaimer} <button type="button" data-testid="historical-open-report" className="underline decoration-jigsaw-orange underline-offset-2" onClick={() => navigate("/control-centre/reports?tab=legacy")}>Open the legacy archive report</button></p>
          </section>
        )}

        {/* Hero: Westminster artwork recreated from the supplied creative (see public/brand/artwork/PROVENANCE.md); live DOM text. */}
        <section data-testid="dashboard-hero" className="relative min-h-[175px] overflow-hidden rounded-[10px] bg-jigsaw-navy text-white md:h-[175px]">
          <img src="/brand/artwork/hero-london.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover object-[center_60%]" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(15,23,48,0.88)_0%,rgba(15,23,48,0.55)_55%,rgba(15,23,48,0.25)_100%)]" />
          <div className="relative flex h-full flex-col justify-between p-5 md:px-[44px] md:py-6">
            <div>
              <p className="text-[26px] font-bold leading-[1.1] md:text-[32px]">Accommodation<br />for brighter tomorrows.</p>
              <p className="mt-1.5 text-[14px] text-white/85">Supporting people. Strengthening communities.</p>
            </div>
            <img src="/brand/jigsaw-logo-full.png" alt="Jigsaw Conferences Ltd" className="mt-4 hidden h-7 w-auto self-start object-contain brightness-0 invert md:block" />
          </div>
          <blockquote data-testid="hero-quote" className="relative mx-5 mb-5 max-w-[280px] border-l-2 border-jigsaw-orange pl-4 text-[18px] font-medium leading-snug text-white md:absolute md:bottom-6 md:right-[44px] md:m-0">
            &ldquo;A safe place to stay can be the start of a brighter future.&rdquo;
          </blockquote>
        </section>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Panel title="Booking status" testId="booking-status-panel">
            <StatusDonut segments={data.booking_status_breakdown || []} total={totalBookings} />
          </Panel>
          <Panel title="Enquiry trend" testId="enquiry-trend-panel" action={<span className="text-xs text-jigsaw-muted">30 days · {trendTotal}</span>}>
            <TrendBars points={data.enquiry_trend_30d || []} />
          </Panel>
          <Panel title="UK locations" testId="locations-panel" action={<span className="text-xs text-jigsaw-muted">bookings</span>}>
            <UkLocationsMap locations={data.top_locations || []} />
          </Panel>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Panel title="Tasks" testId="needs-attention-panel" action={<Link to="/control-centre/enquiries" className="text-xs font-medium text-[#3157A4] hover:underline">All enquiries</Link>}>
            {data.needs_attention.length === 0 ? <p className="text-sm text-jigsaw-muted">Nothing needs attention right now.</p> : (
              <ul className="divide-y divide-jigsaw-border">
                {data.needs_attention.slice(0, 6).map((e) => (
                  <li key={e.id} data-testid={`needs-attention-${e.id}`} className="flex min-h-[44px] items-center justify-between gap-3 py-2 text-sm">
                    <button onClick={() => navigate(`/control-centre/enquiries/${e.id}`)} className="min-w-0 text-left hover:underline">
                      <p className="truncate text-jigsaw-navy">{e.reason === "Unassigned" ? "Assign a coordinator" : e.reason}</p>
                      <p className="truncate text-xs text-jigsaw-muted"><span className="font-mono-ref text-jigsaw-navy">{e.reference}</span>{e.client_name ? ` · ${e.client_name}` : ""}{e.location ? ` · ${e.location}` : ""}{e.rooms_required ? ` · ${e.rooms_required} room(s)` : ""}</p>
                    </button>
                    <StatusBadge status={e.status} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Check-ins" testId="upcoming-arrivals-panel" action={<Link to="/control-centre/allocations" className="text-xs font-medium text-[#3157A4] hover:underline">Allocations</Link>}>
            {data.upcoming_arrivals_list.length === 0 ? <p className="text-sm text-jigsaw-muted">No arrivals in the next 7 days.</p> : (
              <ul className="divide-y divide-jigsaw-border">
                {data.upcoming_arrivals_list.slice(0, 6).map((a) => (
                  <li key={a.booking_id + a.check_in_date} className="flex min-h-[44px] items-center justify-between gap-3 py-2 text-sm">
                    <button onClick={() => navigate(`/control-centre/bookings/${a.booking_id}`)} className="min-w-0 text-left hover:underline">
                      <p className="truncate text-jigsaw-navy">{a.property_name || "Property to be allocated"}</p>
                      <p className="truncate text-xs text-jigsaw-muted"><span className="font-mono-ref">{a.booking_reference}</span>{a.guests_count ? ` · ${a.guests_count} guest(s)` : ""}{a.location ? ` · ${a.location}` : ""}</p>
                    </button>
                    <span className="shrink-0 text-xs text-jigsaw-muted">{new Date(a.check_in_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>

      {/* ---------------- right rail (324px in the reference) ---------------- */}
      <aside className="w-full shrink-0 space-y-4 xl:w-[324px]">
        <Panel title="Assistant" testId="assistant-panel" action={<Sparkles className="h-4 w-4 text-jigsaw-orange" />}>
          <p data-testid="assistant-state" className="text-sm text-jigsaw-muted">
            {ai?.status === "CONNECTED" ? "Ask about enquiries, bookings and suppliers." : "AI assistance is currently unavailable in this environment."}
          </p>
          <div className="mt-3 flex h-10 items-center gap-2 rounded-lg border border-jigsaw-border bg-jigsaw-bg px-3 text-sm text-jigsaw-muted"><Search className="h-4 w-4" /><span className="truncate">Ask the assistant…</span></div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {["Summarise today", "Unassigned enquiries", "Expiring compliance"].map((t) => <span key={t} className="rounded-full border border-jigsaw-border px-2.5 py-1 text-[11px] text-jigsaw-muted">{t}</span>)}
          </div>
          <Link to="/control-centre/ai" className="mt-3 inline-flex text-xs font-medium text-[#3157A4] hover:underline">AI settings and gates</Link>
        </Panel>
        <Panel title="Quick actions" testId="quick-actions-panel">
          <div className="grid grid-cols-2 gap-2">
            {QUICK_ACTIONS.map(({ label, icon: Icon, to, testId }) => (
              <Link key={to} to={to} data-testid={testId} className="flex h-[64px] flex-col items-center justify-center gap-1.5 rounded-md border border-jigsaw-border px-2 text-center text-[11px] font-medium text-jigsaw-navy hover:border-jigsaw-orange/50 hover:bg-jigsaw-bg">
                <Icon strokeWidth={1.75} className="h-4 w-4 text-jigsaw-orange" />{label}
              </Link>
            ))}
          </div>
        </Panel>
        <Panel title="Recent activity" testId="recent-activity-panel">
          {(() => { const ops = data.recent_activity.map((a) => ({ ...a, label: describeActivity(a) })).filter((a) => a.label).slice(0, 6); return ops.length === 0 ? (
            <p className="text-sm text-jigsaw-muted">No operational activity recorded yet. Sign-in events are listed under Admin → Security audit.</p>
          ) : (
            <ul className="space-y-3">
              {ops.map((a) => (
                <li key={a.id} className="flex gap-3 text-sm">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-jigsaw-orange" />
                  <div className="min-w-0">
                    <p className="truncate text-jigsaw-navy">{a.label}</p>
                    <p className="text-xs text-jigsaw-muted">{new Date(a.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </li>
              ))}
            </ul>
          ); })()}
        </Panel>
      </aside>
    </div>
      <footer data-testid="dashboard-footer" className="mt-8 flex flex-col gap-1 border-t border-jigsaw-border pt-4 text-[11px] text-jigsaw-muted sm:flex-row sm:justify-between">
        <span>&copy; {new Date().getFullYear()} Jigsaw Conferences Ltd</span>
        <span>People &nbsp;|&nbsp; Places &nbsp;|&nbsp; Positive Change</span>
      </footer>
    </div>
  );
}
